"use strict";

class ProductsDataLayer {
  constructor() {
    this.dataLayer = window.dataLayer || [];
    this.selectors = {
      productGtm: ".card-product_gtm",
      productWrapper: ".card-wrapper",
      cartRecommendedProducts: "[data-cart-recommended-products]",
      recommendedProducts: "product-recommendations",
    };

    this.cartItems = [];
    this.init();
  }

  init() {
    this.bind();
    this.loadCurrentCart();
    this.getProductsLists();
    this.getSingleProduct();
    this.getCartViewItems();
  }

  bind() {
    document.addEventListener("datalayer-pagination-products", (e) => {
      this.paginationDynamicProducts(e.detail);
    });

    document.addEventListener("related-products-loaded", () => {
      this.getDynamicProducts(this.selectors.cartRecommendedProducts);
    });

    document.addEventListener("recommended-products-loaded", () => {
      this.getDynamicProducts(this.selectors.recommendedProducts);
    });

    document.addEventListener("cart-product-added", (e) => {
      this.setProductGtmData(
        ".card-wrapper",
        e.detail.element,
        e.detail.response,
        "add_to_cart"
      );
    });

    document.addEventListener("single-product-add-to-cart", (e) => {
      const form = e.detail.element;
      const response = e.detail.response;
      const formParent = form.closest(".combine-content")
        ? ".combine-content"
        : ".buy-buttons .product-form";

      this.setProductGtmData(formParent, form, response, "add_to_cart");
    });

    document.addEventListener("cart-updated", (e) => {
      this.updateCartProducts(e.detail.element, e.detail.response);
    });

    document.addEventListener("initial-cart-updated", async () => {
      await this.loadCurrentCart();
    });
  }

  async loadCurrentCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + "cart.js");
      const data = await response.json();
      this.cartItems = data?.items || [];
    } catch (error) {
      console.error("Error loading cart data:", error);
      this.cartItems = [];
    }
  }

  resetEcommerce() {
    this.dataLayer.push({ ecommerce: null });
  }

  parseGtmData(gtmData) {
    try {
      const parsed = gtmData ? JSON.parse(gtmData) : null;

      if (parsed?.item_id != null) {
        parsed.item_id = String(parsed.item_id);
      }

      return parsed;
    } catch (error) {
      console.error("Error parsing GTM data:", error);
      return null;
    }
  }

  cleanObject(object) {
    return Object.fromEntries(
      Object.entries(object).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
  }

  formatPrice(price) {
    if (price == null || Number.isNaN(Number(price))) return 0;
    return +(Number(price) / 100).toFixed(2);
  }

  normalizeItem(item, index = 0) {
    if (!item) return null;

    return this.cleanObject({
      index: item.index || index + 1,
      item_id: item.item_id != null ? String(item.item_id) : undefined,
      item_name: item.item_name,
      item_brand: item.item_brand,
      item_category: item.item_category,
      item_category2: item.item_category2,
      item_category3: item.item_category3,
      item_category4: item.item_category4,
      item_category5: item.item_category5,
      price: item.price,
      discount: item.discount || 0,
    });
  }

  normalizeCartItem(item, index = 0) {
    const normalized = this.normalizeItem(item, index);
    if (!normalized) return null;

    return this.cleanObject({
      ...normalized,
      quantity: item.quantity,
      item_variant: item.item_variant,
      coupon: item.coupon,
    });
  }

  getProductDiscount(response) {
    const allocations = response?.line_level_discount_allocations || [];

    if (!allocations.length) return {};

    const discountAmount = allocations.reduce((total, allocation) => {
      return total + this.formatPrice(allocation.amount || 0);
    }, 0);

    const coupon = allocations
      .map((allocation) => allocation.discount_application?.title)
      .filter(Boolean)
      .join(", ");

    return this.cleanObject({
      discount: +discountAmount.toFixed(2),
      coupon,
    });
  }

  getProductsLists() {
    const productLists = [
      "#product-grid",
      ".featured-products",
      ".related-products",
      ".cross-selling-products",
      ".gift-products-grid",
    ];

    productLists.forEach((selector) => {
      const productElements = document.querySelectorAll(selector);
      this.processProductElements(productElements);
    });
  }

  paginationDynamicProducts(loadedItems) {
    const loadedProductElements = loadedItems.filter((product) =>
      product.querySelector(this.selectors.productGtm)
    );

    this.processProductElements(loadedProductElements);
  }

  getDynamicProducts(selector) {
    const productElements = document.querySelectorAll(selector);
    this.processProductElements(productElements);
  }

  processProductElements(productElements) {
    let allItems = [];

    productElements.forEach((productList) => {
      const items = Array.from(
        productList.querySelectorAll(this.selectors.productGtm)
      )
        .map((product) => this.parseGtmData(product.getAttribute("data-gtm")))
        .filter(Boolean);

      if (!items.length) return;

      allItems.push(...items);
      this.attachClickEvent(productList);
    });

    if (allItems.length) {
      this.addDataLayerForItemList(allItems);
    }
  }

  attachClickEvent(productList) {
    productList
      .querySelectorAll(this.selectors.productWrapper)
      .forEach((product) => {
        if (product.dataset.gtmClickBound === "true") return;

        product.dataset.gtmClickBound = "true";

        product.addEventListener("click", () => {
          const gtmData = this.getGtmData(product);
          if (gtmData) this.updateDataLayerForItemSelect(gtmData);
        });
      });
  }

  getGtmData(product) {
    const gtmDataAttribute = product
      ?.querySelector(this.selectors.productGtm)
      ?.getAttribute("data-gtm");

    return this.parseGtmData(gtmDataAttribute);
  }

  addDataLayerForItemList(items) {
    const cleanedItems = items
      .slice(0, 36)
      .map((item, index) => this.normalizeItem(item, index))
      .filter(Boolean);

    if (!cleanedItems.length) return;

    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_id: items[0]?.item_list_id || "category_page",
        item_list_name: items[0]?.item_list_name || "Category Page",
        currency: items[0]?.currency || "EUR",
        items: cleanedItems,
      },
    });
  }

  updateDataLayerForItemSelect(gtmData) {
    const cleanedItem = this.normalizeItem(gtmData, gtmData.index || 1);

    if (!cleanedItem) return;

    this.resetEcommerce();
    this.dataLayer.push({
      event: "select_item",
      ecommerce: {
        item_list_id: gtmData.item_list_id || "category_page",
        item_list_name: gtmData.item_list_name || "Category Page",
        currency: gtmData.currency || "EUR",
        items: [cleanedItem],
      },
    });
  }

  getSingleProduct() {
    const product =
      document.querySelector(".buy-buttons .product-form") ||
      document.querySelector("[data-product-form]") ||
      document.querySelector("product-form");

    const gtmData = this.getGtmData(product);
    const cleanedItem = this.normalizeItem(gtmData, 0);

    if (!cleanedItem) return;

    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_item",
      ecommerce: {
        currency: gtmData.currency || "EUR",
        value: gtmData.price,
        items: [cleanedItem],
      },
    });
  }

  setProductGtmData(parentSelector, item, response, event) {
    if (!item || !response) return;

    const responseData = response;
    const product = item.closest(parentSelector);
    const gtmData = this.getGtmData(product) || {};
    const variant = responseData.options_with_values?.[0];
    const totalPrice = this.formatPrice(responseData.final_line_price || 0);
    const discounts = this.getProductDiscount(responseData);

    const cleanedItem = this.cleanObject({
      ...this.normalizeItem(gtmData, gtmData.index || 1),
      ...discounts,
      quantity: responseData.quantity,
      item_variant: variant
        ? `${variant.name}: ${variant.value}`
        : responseData.variant_title || gtmData.item_variant,
    });

    this.resetEcommerce();
    this.dataLayer.push({
      event,
      ecommerce: {
        currency: gtmData.currency || "EUR",
        value: totalPrice,
        items: [cleanedItem],
      },
    });

    this.loadCurrentCart();
  }

  updateCartProducts(element, response) {
    if (!response || !element) return;

    const addedItem = response.items_added?.[0];
    const removedItem = response.items_removed?.[0];
    const item = addedItem || removedItem;

    if (!item) return;

    const updatedItemId = item.variant_id;

    const updatedProduct =
      response.items?.find((cartItem) => cartItem.variant_id === updatedItemId) ||
      this.cartItems?.find((cartItem) => cartItem.variant_id === updatedItemId);

    if (!updatedProduct) {
      console.warn("GTM cart event skipped: product data not found", {
        updatedItemId,
        item,
      });
      return;
    }

    if (addedItem) {
      this.setProductGtmData(
        ".cart-item",
        element,
        updatedProduct,
        "add_to_cart"
      );
    }

    if (removedItem) {
      this.setProductGtmData(
        ".cart-item",
        element,
        updatedProduct,
        "remove_from_cart"
      );
    }
  }

  getCartViewItems() {
    const cartPage = document.querySelector("#main-cart-items");
    if (!cartPage) return;

    const scriptEl = document.querySelector("script#datalayer-cart-items");
    if (!scriptEl) return;

    let cartData;

    try {
      cartData = JSON.parse(scriptEl.innerText);
    } catch (error) {
      console.error("Error parsing datalayer-cart-items:", error);
      return;
    }

    if (!Array.isArray(cartData) || !cartData.length) return;

    cartData = cartData[0];

    cartData.items = (cartData.items || [])
      .map((item, index) => {
        item.price = this.formatPrice(item.price);

        if (item.line_level_discount_allocations?.length) {
          item.line_level_discount_allocations =
            item.line_level_discount_allocations.map((discount) => {
              return {
                amount: discount.amount,
                discount_application: {
                  value_type: discount.value_type,
                  value: discount.value,
                  title: discount.title,
                },
              };
            });
        }

        const discountInfo = this.getProductDiscount(item);

        item.discount = discountInfo.discount;
        item.coupon = discountInfo.coupon;

        delete item.line_level_discount_allocations;
        delete item.final_price;

        return this.normalizeCartItem(item, index);
      })
      .filter(Boolean);

    cartData.value = this.formatPrice(cartData.value);
    cartData.currency = cartData.currency || "EUR";

    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_cart",
      ecommerce: cartData,
    });
  }
}

document.addEventListener("DOMContentLoaded", () => new ProductsDataLayer());